import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, AlertTriangle, Radio, ArrowRight, Check, RefreshCw, Wifi,
} from 'lucide-react';
import {
  fetchSimReconciliation, markChargerAs1nce,
  type SimReconciliationRow, type SimReconciliationOrphan,
} from '@/lib/api';

/**
 * Phase 5a-patch Item 5 — SIM reconciliation report.
 *
 * Cross-references the 1nce device list against Charger docs and
 * surfaces three buckets the operator can act on:
 *
 *   provisioning_misses   — chargers marked 1nce but not in 1nce's
 *                           inventory (likely provisioning mistake)
 *   migration_candidates  — chargers with ICCIDs that DO appear in
 *                           1nce inventory but are not yet flagged
 *                           sim_provider: '1nce'. One-click migrate.
 *   orphan_sims           — 1nce SIMs whose ICCIDs aren't linked
 *                           to any Charger doc
 *
 * Backed by GET /admin/sim-reconciliation with a 5-minute server-
 * side cache to avoid hammering the 1nce API.
 */

interface SimReconciliationPageProps {
  onNavigate: (path: string) => void;
}

type Tab = 'candidates' | 'misses' | 'orphans';

function fmtDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function SimReconciliationPage({ onNavigate }: SimReconciliationPageProps) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('candidates');
  const [markedToast, setMarkedToast] = useState<string | null>(null);

  const reportQ = useQuery({
    queryKey: ['sim-reconciliation'],
    queryFn: fetchSimReconciliation,
    // staleTime under the server's 5-min cache window so React Query
    // doesn't refire the request on every tab switch.
    staleTime: 4 * 60 * 1000,
  });

  const markM = useMutation({
    mutationFn: (chargerId: string) => markChargerAs1nce(chargerId),
    onSuccess: (data, chargerId) => {
      setMarkedToast(
        `Marked ${chargerId.slice(-8)} as 1nce-managed. Status: ${data.immediate_fetch.status ?? 'pending'}`,
      );
      void qc.invalidateQueries({ queryKey: ['sim-reconciliation'] });
      setTimeout(() => setMarkedToast(null), 3500);
    },
    onError: (err: unknown) => {
      setMarkedToast(`Failed: ${err instanceof Error ? err.message : 'unknown error'}`);
      setTimeout(() => setMarkedToast(null), 4500);
    },
  });

  return (
    <div style={{ padding: '24px 28px' }}>
      <header style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>
            <Radio size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            SIM reconciliation
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
            Migration assist for chargers transitioning to 1nce-managed SIM connectivity.
          </p>
        </div>
        <button
          type="button"
          onClick={() => qc.invalidateQueries({ queryKey: ['sim-reconciliation'] })}
          disabled={reportQ.isFetching}
          style={{
            padding: '6px 12px', fontSize: 12,
            background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))',
            borderRadius: 6, cursor: 'pointer', color: 'var(--color-tx)',
          }}
        >
          <RefreshCw size={11} className={reportQ.isFetching ? 'spin' : undefined} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Refresh
        </button>
      </header>

      {reportQ.isLoading && (
        <p><Loader2 size={14} className="spin" /> Loading 1nce device list + reconciling…</p>
      )}
      {reportQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} /> {reportQ.error instanceof Error ? reportQ.error.message : 'Could not load report.'}
        </p>
      )}

      {reportQ.data && (
        <>
          <p style={{ fontSize: 12, color: 'var(--color-tx-m)', marginBottom: 8 }}>
            {reportQ.data.total_1nce_devices} SIMs in 1nce inventory · cached at {fmtDate(reportQ.data.cached_at)}
          </p>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 4, marginBottom: 16,
            borderBottom: '1px solid var(--color-brd, var(--evi-border))',
          }}>
            {([
              ['candidates', 'Migration candidates', reportQ.data.migration_candidates.length, 'var(--evi-accent, #2ECC71)'],
              ['misses', 'Provisioning misses', reportQ.data.provisioning_misses.length, 'var(--evi-warning, #f59e0b)'],
              ['orphans', 'Orphan SIMs', reportQ.data.orphan_sims.length, 'var(--color-tx-m)'],
            ] as const).map(([k, label, count, color]) => {
              const active = tab === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k as Tab)}
                  style={{
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
                    color: active ? color : 'var(--color-tx-m)',
                    fontSize: 13, fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    marginBottom: -1,
                  }}
                >
                  {label} <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {tab === 'candidates' && (
            <CandidatesTab
              rows={reportQ.data.migration_candidates}
              onMark={(id) => markM.mutate(id)}
              isMarking={markM.isPending}
              onNavigate={onNavigate}
            />
          )}
          {tab === 'misses' && <MissesTab rows={reportQ.data.provisioning_misses} onNavigate={onNavigate} />}
          {tab === 'orphans' && <OrphansTab rows={reportQ.data.orphan_sims} />}
        </>
      )}

      {markedToast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          padding: '10px 14px',
          background: 'var(--color-sf, #111)',
          border: '1px solid var(--evi-accent, #2ECC71)',
          borderRadius: 8, fontSize: 13, maxWidth: 360,
          boxShadow: '0 4px 18px rgba(0,0,0,0.4)',
        }}>
          {markedToast}
        </div>
      )}
    </div>
  );
}

function CandidatesTab({
  rows, onMark, isMarking, onNavigate,
}: {
  rows: SimReconciliationRow[];
  onMark: (chargerId: string) => void;
  isMarking: boolean;
  onNavigate: (path: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
        No migration candidates. All chargers with ICCIDs found in 1nce inventory are already flagged
        <code style={{ marginLeft: 4 }}>sim_provider: '1nce'</code>.
      </p>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
          <th style={{ padding: 8 }}>Charger</th>
          <th style={{ padding: 8 }}>Location</th>
          <th style={{ padding: 8 }}>ICCID</th>
          <th style={{ padding: 8 }}>Current provider</th>
          <th style={{ padding: 8, textAlign: 'right' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.charger_id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
            <td style={{ padding: 8 }}>
              <strong>{r.display_name ?? r.identity}</strong>
              {r.display_name && (
                <div style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>{r.identity}</div>
              )}
            </td>
            <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>{r.location_name ?? '—'}</td>
            <td style={{ padding: 8 }}><code>{r.sim_iccid}</code></td>
            <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>
              {r.current_sim_provider ?? <em>unset</em>}
            </td>
            <td style={{ padding: 8, textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => onMark(r.charger_id)}
                disabled={isMarking}
                style={{
                  padding: '6px 10px', fontSize: 11,
                  background: 'var(--evi-accent, #2ECC71)', color: '#000',
                  border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
                  marginRight: 6,
                }}
              >
                <Check size={11} style={{ verticalAlign: 'middle' }} /> Mark as 1nce-managed
              </button>
              <button
                type="button"
                onClick={() => onNavigate(`/chargers/${r.charger_id}`)}
                style={{
                  padding: '6px 10px', fontSize: 11,
                  background: 'transparent', color: 'var(--color-tx-m)',
                  border: '1px solid var(--color-brd, var(--evi-border))',
                  borderRadius: 6, cursor: 'pointer',
                }}
              >
                Open <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MissesTab({
  rows, onNavigate,
}: {
  rows: SimReconciliationRow[];
  onNavigate: (path: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
        No provisioning misses. Every 1nce-managed charger has a matching SIM in 1nce inventory.
      </p>
    );
  }
  return (
    <>
      <p style={{ fontSize: 12, color: 'var(--color-tx-m)', marginBottom: 8 }}>
        These chargers are flagged <code>sim_provider: '1nce'</code> but their ICCID isn&apos;t in the 1nce device list.
        Likely causes: wrong ICCID, SIM not yet provisioned on 1nce, or 1nce-side de-provisioning.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
            <th style={{ padding: 8 }}>Charger</th>
            <th style={{ padding: 8 }}>Location</th>
            <th style={{ padding: 8 }}>ICCID</th>
            <th style={{ padding: 8 }}>Last 1nce event</th>
            <th style={{ padding: 8, textAlign: 'right' }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.charger_id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
              <td style={{ padding: 8 }}>
                <strong>{r.display_name ?? r.identity}</strong>
                {r.display_name && <div style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>{r.identity}</div>}
              </td>
              <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>{r.location_name ?? '—'}</td>
              <td style={{ padding: 8 }}><code>{r.sim_iccid}</code></td>
              <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>{fmtDate(r.last_1nce_event_at)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => onNavigate(`/chargers/${r.charger_id}`)}
                  style={{
                    padding: '6px 10px', fontSize: 11,
                    background: 'transparent', color: 'var(--color-tx-m)',
                    border: '1px solid var(--color-brd, var(--evi-border))',
                    borderRadius: 6, cursor: 'pointer',
                  }}
                >
                  Investigate <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function OrphansTab({ rows }: { rows: SimReconciliationOrphan[] }) {
  if (rows.length === 0) {
    return (
      <p style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
        No orphan SIMs. Every SIM in 1nce inventory is linked to a Charger doc.
      </p>
    );
  }
  return (
    <>
      <p style={{ fontSize: 12, color: 'var(--color-tx-m)', marginBottom: 8 }}>
        SIMs in 1nce inventory not linked to any Charger doc. Most likely: unassigned SIMs in inventory,
        chargers that haven&apos;t sent a BootNotification with ICCID yet, or chargers from a different tenant.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
            <th style={{ padding: 8 }}>ICCID</th>
            <th style={{ padding: 8 }}>IMSI</th>
            <th style={{ padding: 8 }}>Status</th>
            <th style={{ padding: 8 }}>Label</th>
            <th style={{ padding: 8 }}>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.iccid} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
              <td style={{ padding: 8 }}><code>{r.iccid}</code></td>
              <td style={{ padding: 8, color: 'var(--color-tx-m)' }}><code>{r.imsi ?? '—'}</code></td>
              <td style={{ padding: 8 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 6px', fontSize: 11, borderRadius: 4,
                  background: r.status === 'ENABLED' || r.status === 'ATTACHED'
                    ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 16%, transparent)'
                    : 'color-mix(in srgb, var(--color-tx-m) 16%, transparent)',
                  color: r.status === 'ENABLED' || r.status === 'ATTACHED'
                    ? 'var(--evi-accent, #2ECC71)'
                    : 'var(--color-tx-m)',
                }}>
                  <Wifi size={10} /> {r.status ?? '—'}
                </span>
              </td>
              <td style={{ padding: 8 }}>{r.label ?? '—'}</td>
              <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>{fmtDate(r.last_seen_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
